import { SKIP_PAYMENT_FEE_CENTS } from '@/constants/paymentPolicy';
import { calendarDateOnly } from '@/utils/formatScheduleDate';

const INELIGIBLE_MESSAGES = {
  no_pending_installments: 'There are no upcoming installments left to skip.',
  not_enough_payments_left: "You're on your final payment, so there's no later installment to absorb a skip.",
  past_due_blocks_skip: 'Pay your past-due balance before skipping a payment.',
};

const PAST_DUE_STATUSES = new Set(['declined', 'error', 'delinquent']);

/**
 * Build the before/after skip preview from upcoming schedule rows already
 * rendered in PaymentScheduleDisplay. Matches Stripe skip math: skipped
 * installment → $0, remaining balance re-spread across later rows (same dates).
 *
 * Selects exactly what the charge executes (planNmiSkipPayment in
 * supabase/functions/_shared/nmi-skip-payment-quote.ts — KEEP IN LOCKSTEP):
 * only `pending` payments, earliest due first, and nothing while a payment is
 * past due. It used to take every open row, so a retrying payment could be
 * shown as the one skipped, or as absorbing the skipped amount, while the
 * charge did something else (JRXIL3, 19JIZ7, NKOBHR, 2026-10-28).
 */
export function buildNmiSkipPaymentPreview(upcomingRows, { openInvoicesTotalCents = 0 } = {}) {
  const rows = (upcomingRows || []).filter((r) => r?.date && !r.fee_skipped && !r.paid_ahead);
  const pastDueCents = rows
    .filter((r) => PAST_DUE_STATUSES.has(r._nmi_status))
    .reduce((s, r) => s + (Number(r.amount_cents) || 0), 0);
  if (openInvoicesTotalCents > 0 || pastDueCents > 0) {
    return {
      can_skip: false,
      reason: 'past_due_blocks_skip',
      message: INELIGIBLE_MESSAGES.past_due_blocks_skip,
    };
  }

  const openRows = rows
    // Rows without a status (older callers) count as pending.
    .filter((r) => (r._nmi_status ?? 'pending') === 'pending')
    .map((r) => ({
      date: calendarDateOnly(r.date),
      amount_cents: Number(r.amount_cents) || 0,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  if (openRows.length === 0) {
    return { can_skip: false, reason: 'no_pending_installments', message: INELIGIBLE_MESSAGES.no_pending_installments };
  }

  const perPaymentCents = openRows[0].amount_cents;
  const currentDates = openRows.map((r) => ({ ...r }));

  if (openRows.length < 2) {
    return {
      can_skip: false,
      reason: 'not_enough_payments_left',
      message: INELIGIBLE_MESSAGES.not_enough_payments_left,
      current_schedule: {
        dates: currentDates,
        num_payments: currentDates.length,
        per_payment_cents: perPaymentCents,
      },
    };
  }

  const remainingCents = openRows.reduce((s, r) => s + r.amount_cents, 0);
  const skippedDate = openRows[0].date;
  const skippedAmountCents = openRows[0].amount_cents;
  const afterSkip = openRows.slice(1);
  const newNum = afterSkip.length;
  const newUnit = Math.max(1, Math.ceil(remainingCents / newNum));

  let left = remainingCents;
  const redistributed = afterSkip.map((r, i) => {
    const isLast = i === newNum - 1;
    const amount = isLast ? Math.max(0, left) : Math.min(newUnit, Math.max(0, left));
    left -= amount;
    return { date: r.date, amount_cents: amount };
  });

  return {
    can_skip: true,
    skip_fee_cents: SKIP_PAYMENT_FEE_CENTS,
    skipped_date: skippedDate,
    next_installment_due: skippedDate,
    skipped_amount_cents: skippedAmountCents,
    next_installment_amount_cents: skippedAmountCents,
    new_next_charge_date: redistributed[0]?.date ?? null,
    new_unit_amount_cents: newUnit,
    remaining_after_skip_count: newNum,
    remaining_cents: remainingCents,
    current_schedule: {
      dates: currentDates,
      num_payments: currentDates.length,
      per_payment_cents: perPaymentCents,
    },
    proposed_schedule: {
      dates: [{ date: skippedDate, amount_cents: 0 }, ...redistributed],
      num_payments: newNum,
      per_payment_cents: newUnit,
    },
  };
}

export { INELIGIBLE_MESSAGES };
